// Project Overview: a live hero (requests per minute, connected clients, active users today), the
// KPI row with deltas, the Get started checklist, a card per product with a sparkline, the week's
// story, where requests come from, and the latest activity.
import { useState } from "react";
import { doc, serverTimestamp, updateDoc } from "@estra/client/firestore";
import { api, db, missing, myMail, myPush } from "../../api.ts";
import { Spark } from "../../charts/Lines.tsx";
import { WorldDots } from "../../charts/WorldDots.tsx";
import { ago, fmt } from "../../fmt.ts";
import { invalidate, useLoad, useNow, usePoll } from "../../data/hooks.ts";
import { milestoneLabel, MILESTONES, nowStats, type Metric, type NowStats, type ProjectStats } from "../../data/stats.ts";
import { projectHref } from "../../router.ts";
import { ICONS } from "../../shell/nav.tsx";
import { Celebrate, MinuteStrip, PageHead, ProjectPill, RangePicker, Stats, Tween } from "../../ui.tsx";
import { FeedList, useFeed } from "./Activity.tsx";
import { backendHost } from "../../lib/kit.ts";
import { kpis, METRICS, spanLabel } from "./metrics.tsx";
import { useProject } from "./ProjectPage.tsx";

function Hero() {
  const { id, live } = useProject();
  const [now, setNow] = useState<NowStats | null | undefined>(undefined);
  const tick = useNow(1000);
  usePoll(() => void nowStats(id).then(setNow).catch(() => setNow(null)), 15_000, live);
  const updated = now?.at ? Math.max(0, Math.round((tick - now.at) / 1000)) : null;
  const perMin = now?.perMinute ?? [];
  const lastMin = perMin[perMin.length - 1] ?? 0;
  return (
    <section className="hero" aria-label="Right now">
      <div>
        <span className="live">
          <span className={`live-dot${now?.live ? "" : " off"}`} />Live
          <span className="muted">{!live ? "starts once your backend is live" : now === undefined ? "connecting…" : now?.live ? `updated ${updated ?? 0} s ago` : "the per-minute view starts with your backend's next update"}</span>
        </span>
        <div className="activity-head"><span className="big"><Tween value={lastMin} format={fmt.int} /><small>requests in the last minute</small></span></div>
        <MinuteStrip values={perMin.length ? perMin : Array(60).fill(0)} />
      </div>
      <div>
        <span className="kicker">Right now</span>
        <div className="hero-nums">
          <div className="hero-num"><span className="stat-label">Connected</span><b>{now ? <>≈<Tween value={now.connected} /></> : "–"}</b><span className="stat-note">realtime clients</span></div>
          <div className="hero-num"><span className="stat-label">Active today</span><b>{now ? <Tween value={now.activeToday} /> : "–"}</b><span className="stat-note">signed-in users</span></div>
          <div className="hero-num"><span className="stat-label">Errors · 1h</span><b className={now?.errorsLastHour ? "bad" : ""}>{now ? <Tween value={now.errorsLastHour} /> : "–"}</b><span className="stat-note">server errors</span></div>
        </div>
      </div>
    </section>
  );
}

interface Check { key: string; title: string; why: string; done: boolean; href: string; cta: string; optional?: boolean }

function useChecklist(): Check[] {
  const { id, p, live, stats } = useProject();
  const s = stats.data;
  const mail = useLoad(live ? `mail-domains:${id}` : null, () => myMail(id)("domains").then((r) => r.data as { status: string }[]).catch(() => [] as { status: string }[]), 120_000);
  const push = useLoad(live ? `push-apps:${id}` : null, () => myPush(id)("apps").then((r) => r.data as { ios?: unknown; android?: unknown }[]).catch(() => [] as { ios?: unknown; android?: unknown }[]), 120_000);
  const digest = useLoad("digest", () => api<{ optIn: boolean }>("/v1/stats/digest").catch((e) => (missing(e) ? { optIn: false } : Promise.reject(e))), 300_000);
  const has = (k: string) => !!s?.milestones.some((m) => m.key === k);
  const total = (m: Metric) => s?.kpis[m]?.now ?? 0;
  return [
    { key: "live", title: "Backend live", why: "Your schema, rules, sign-in and realtime at one address.", done: live, href: projectHref(id, "database", "schema"), cta: "Set it up" },
    { key: "doc", title: "Write your first document", why: "See it land in the data browser the moment your app writes it.", done: has("first.doc") || total("docWrites") > 0 || Object.values((s?.facts["collection.docs"] ?? [])).some((f) => f.value > 0), href: projectHref(id, "database", "data"), cta: "Open the data" },
    { key: "user", title: "Sign up your first user", why: "Sign-in works out of the box with email; your users appear here.", done: has("first.user") || total("users") > 0, href: projectHref(id, "auth", "users"), cta: "See users" },
    { key: "provider", title: "Turn on Google, Apple or GitHub", why: "One-tap sign-in roughly doubles how many visitors finish signing up.", done: Object.keys(p.signIn ?? {}).length > 0 || p.keepFirebaseAuth === true, href: projectHref(id, "auth", "providers"), cta: "Add a provider" },
    { key: "origin", title: "Add your web origin", why: "Browsers only get answers from your backend on the sites you list.", done: ((p.origins ?? []) as string[]).length > 0, href: projectHref(id, "auth", "settings"), cta: "Add a domain" },
    { key: "push_config", title: "Ship a change with estra push", why: "Change your schema and rules from your repository in seconds.", done: has("first.push_config"), href: projectHref(id, "database", "schema"), cta: "Show me how" },
    { key: "mail", title: "Send email from your domain", why: "Sign-in links and receipts from you, not from us.", done: !!mail.data?.some((d) => d.status === "verified"), href: projectHref(id, "mail", "domains"), cta: "Add a domain", optional: true },
    { key: "push", title: "Set up push notifications", why: "Bring people back when something happens.", done: !!push.data?.some((a) => a.ios || a.android), href: projectHref(id, "push", "apps"), cta: "Add an app", optional: true },
    { key: "digest", title: "Get the Monday digest", why: "One email a week with how your app did.", done: !!digest.data?.optIn, href: projectHref(id, "story"), cta: "Turn it on" },
  ];
}

function GetStarted() {
  const { id, p } = useProject();
  const all = useChecklist();
  const dismissed = (p.kit ?? {}) as Record<string, boolean>;
  const items = all.filter((c) => !dismissed[`setup_${c.key}`]);
  const done = all.filter((c) => c.done || dismissed[`setup_${c.key}`]).length;
  const [open, setOpen] = useState(false);
  if (done === all.length && !open) return <div className="setup-done"><span className="good">✓</span> Setup {done}/{all.length} <button className="linkish" onClick={() => setOpen(true)}>Show</button></div>;
  const dismiss = (key: string) => void updateDoc(doc(db, `projects/${id}`), { [`kit.setup_${key}`]: true, updatedAt: serverTimestamp() }).catch(() => null);
  return (
    <>
      <div className="section-head"><h2>Get started</h2><span className="row small muted"><span className="ring" style={{ ["--p" as string]: Math.round((100 * done) / all.length) }}><span>{done}/{all.length}</span></span>{all.length - done} to go</span></div>
      <div className="setup">
        {items.map((c, i) => (
          <article key={c.key} className={`setup-card${c.done ? " done" : ""}`}>
            <span className="setup-num">{c.done ? "✓ DONE" : `${String(i + 1).padStart(2, "0")}${c.optional ? " · OPTIONAL" : ""}`}</span>
            <h3>{c.title}</h3>
            <p className="small muted">{c.why}</p>
            {!c.done && <a className="btn btn-small" href={c.href}>{c.cta}</a>}
            {!c.done && c.optional && <button className="icon-btn x" title="Not for this project" aria-label={`Dismiss ${c.title}`} onClick={() => dismiss(c.key)}>{ICONS.close}</button>}
          </article>
        ))}
      </div>
    </>
  );
}

function Products({ s }: { s: ProjectStats }) {
  const { id } = useProject();
  const card = (product: string, label: string, metric: Metric, note: string) => {
    const v = s.kpis[metric]?.now ?? (s.series[metric] ?? []).reduce((a, b) => a + b, 0);
    return (
      <a className="product" href={projectHref(id, product)} key={product}>
        <span className="product-name">{ICONS[product]}{label}</span>
        <span className="product-num">{METRICS[metric].format(v)}</span>
        <span className="small muted">{note}</span>
        <Spark values={s.series[metric] ?? []} color="var(--s1)" />
      </a>
    );
  };
  return (
    <div className="products">
      {card("database", "Database", "docReads", `document reads ${spanLabel(s.range)}`)}
      {card("auth", "Authentication", "activeUsers", "active users")}
      {card("storage", "Storage", "fileBytes", "stored")}
      {card("realtime", "Realtime", "rtOpens", "realtime connections")}
      {card("mail", "Mail", "emails", "emails sent")}
      {card("push", "Push", "pushes", "notifications sent")}
    </div>
  );
}

export function Overview() {
  const { id, p, live, range, setRange, stats } = useProject();
  const s = stats.data;
  const feed = useFeed(id, live);
  const [hidden, setHidden] = useState<string[]>([]);
  const fresh = s?.milestones.filter((m) => !m.seen && !hidden.includes(m.key)) ?? [];
  const seen = (keys: string[]) => {
    setHidden((h) => [...h, ...keys]);
    void api(`/v1/stats/${encodeURIComponent(id)}/seen`, { keys }).then(() => invalidate(`stats:${id}`)).catch(() => null);
  };
  const countries = (s?.facts["country.requests"] ?? []).map((f) => ({ country: f.dim, n: f.value }));
  return (
    <div className="page">
      <PageHead kicker={<>Project Overview <ProjectPill p={p} /></>} title={p.name}>
        <span className="mono small muted">{backendHost(p)}</span>
        <RangePicker value={range} onChange={setRange} />
      </PageHead>
      {fresh[0] && <Celebrate title={`Milestone: ${milestoneLabel(fresh[0])}`} sub={MILESTONES[fresh[0].key]?.hint} onClose={() => seen(fresh.map((m) => m.key))} />}
      <Hero />
      <Stats items={kpis(s, ["requests", "docReads", "docWrites", "activeUsers"], { requests: projectHref(id, "usage"), docReads: projectHref(id, "database", "usage"), docWrites: projectHref(id, "database", "usage"), activeUsers: projectHref(id, "auth", "usage") })} />
      <Stats quiet items={kpis(s, ["docDeletes", "emails", "pushes", "errors"], { emails: projectHref(id, "mail"), pushes: projectHref(id, "push") })} />
      <GetStarted />
      {s && (
        <>
          <div className="section-head"><h2>Products</h2><a className="small" href={projectHref(id, "usage")}>All usage →</a></div>
          <Products s={s} />
        </>
      )}
      <div className="grid2">
        <section className="card">
          <div className="card-head"><div><h2>This week</h2><p className="small muted">Your app in sentences, compared with the week before.</p></div><a className="small" href={projectHref(id, "story")}>Story →</a></div>
          {s?.story.length ? (
            <ul className="story">{s.story.slice(0, 3).map((l, i) => <li key={i}><span className={`tone ${l.tone}`}>{l.tone === "good" ? "▲" : l.tone === "bad" ? "▼" : "•"}</span><span>{l.text}</span><a className="go" href={l.href}>Look →</a></li>)}</ul>
          ) : <p className="muted small">{s?.fallback ? "Your story starts once your backend is updated for the new console." : "A story needs a week of numbers. Check back in a few days."}</p>}
        </section>
        <section className="card">
          <div className="card-head"><div><h2>Latest activity</h2><p className="small muted">{feed.state === "ok" ? `Live · ${ago(feed.updated)}` : "Writes and sign-ups as they happen."}</p></div><a className="small" href={projectHref(id, "activity")}>Activity →</a></div>
          {feed.rows.length ? <FeedList list={feed.rows} limit={6} id={id} /> : <p className="muted small">{!live ? "Starts once your backend is live." : feed.state === "unavailable" ? "The live feed starts with your backend's next update." : "Quiet right now."}</p>}
        </section>
      </div>
      <section className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><div><h2>Where requests come from</h2><p className="small muted">By country, {s ? spanLabel(s.range) : ""}.</p></div></div>
        {countries.length ? <WorldDots countries={countries} label="requests" /> : <p className="muted small">The map fills in as requests arrive{s?.fallback ? ", starting with your backend's next update" : ""}.</p>}
      </section>
    </div>
  );
}
